import React, { useState, useEffect, useRef } from "react";
import MaterialTable from "material-table";
import { Typography, Grid, Card, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { colors } from "../components/Theme/WebTheme";
import { useNavigate } from "react-router-dom";
import { FONT_FAMILY, MAIN_COLOR, SECONDORY_COLOR } from "../common/sharedFunctions";
import { api } from "common";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";

const UserWalletDetails = (props) => {
    const { data } = props;
    const { t, i18n } = useTranslation();
    const { fetchUserWalletHistory, adjustWalletAsAdmin } = api;
    const isRTL = i18n.dir();
    const settingsdata = useSelector((state) => state.settingsdata);
    const [selectedRow, setSelectedRow] = useState(null);
    const [settings, setSettings] = useState({});
    const navigate = useNavigate();
    const walletHistory = useSelector((state) => state.auth.walletHistory);
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [walletBalance, setWalletBalance] = useState(data?.walletBalance ?? 0);
    const [debitAmount, setDebitAmount] = useState('');
    const [debitReason, setDebitReason] = useState('');
    const [debitConfirmationOpen, setDebitConfirmationOpen] = useState(false);
    const [debitSubmitting, setDebitSubmitting] = useState(false);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
    const debitIdempotencyKey = useRef(null);
    const canDebitWallet = auth.profile?.usertype === 'admin' && props.id === 0;

    useEffect(() => {
        setWalletBalance(data?.walletBalance ?? 0);
    }, [data?.walletBalance]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        dispatch(fetchUserWalletHistory(data?.id));
    }, [data?.id, dispatch, fetchUserWalletHistory]);

    const formattedBalance = (balance) => settings.swipe_symbol
        ? `${balance} ${settings.symbol || ''}`.trim()
        : `${settings.symbol || ''} ${balance}`.trim();

    const parsedDebitAmount = Number(debitAmount);
    const currentBalance = Number(walletBalance) || 0;
    const projectedBalance = currentBalance - parsedDebitAmount;

    const openDebitConfirmation = () => {
        if (!Number.isFinite(parsedDebitAmount) || parsedDebitAmount <= 0) {
            setCommonAlert({ open: true, msg: 'Lütfen geçerli ve pozitif bir tutar girin.' });
            return;
        }
        if (parsedDebitAmount > currentBalance) {
            setCommonAlert({ open: true, msg: 'Düşülecek tutar mevcut bakiyeyi aşamaz.' });
            return;
        }
        if (!debitReason.trim()) {
            setCommonAlert({ open: true, msg: 'Düşüm nedeni zorunludur.' });
            return;
        }
        setDebitConfirmationOpen(true);
    };

    const confirmDebit = async () => {
        if (debitSubmitting) {
            return;
        }
        debitIdempotencyKey.current = debitIdempotencyKey.current || `admin-debit-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        setDebitSubmitting(true);
        try {
            const result = await dispatch(adjustWalletAsAdmin({
                uid: data.id,
                amount: parsedDebitAmount,
                operation: 'debit',
                reason: debitReason.trim(),
                idempotencyKey: debitIdempotencyKey.current,
            }));
            setWalletBalance(result.walletBalance);
            debitIdempotencyKey.current = null;
            setDebitAmount('');
            setDebitReason('');
            setDebitConfirmationOpen(false);
            setCommonAlert({ open: true, msg: t('success') });
        } catch (error) {
            setCommonAlert({ open: true, msg: error?.message || 'Bakiye düşümü başarısız oldu.' });
        } finally {
            setDebitSubmitting(false);
        }
    };

    const columns = [
        {
            title: t("requestDate"),
            field: "date",
            render: (rowData) =>
                rowData.date ? moment(rowData.date).format("lll") : null,
        },
        {
            title: t("amount"),
            field: "amount",
            editable: "never",
            render: (rowData) =>
                rowData.amount
                    ? settings.swipe_symbol
                        ? rowData.amount + " " + settings.symbol
                        : settings.symbol + " " + rowData.amount
                    : settings.swipe_symbol
                        ? "0 " + settings.symbol
                        : settings.symbol + " 0",
        },
        {
            title: t("transaction_id"),
            field: "transaction_id",
            render: (rowData) =>
                rowData.transaction_id ? rowData.transaction_id : rowData.txRef,
        },
        {
            title: t("type"),
            field: "type",
            render: (rowData) => (
                <div
                    style={{
                        backgroundColor:
                            rowData.type === "Debit"
                                ? colors.RED
                                : rowData.type === "Credit"
                                    ? colors.GREEN
                                    : colors.YELLOW,
                        color: "white",
                        padding: 7,
                        borderRadius: "15px",
                        fontWeight: "bold",
                        width: "150px",
                        margin: "auto",
                    }}
                >
                    {t(rowData.type)}
                </div>
            ),
        },
    ];

    return (

        <Card
            style={{
                borderRadius: "19px",
                marginTop: 20,
                marginBottom: 20,
                boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            }}
        >
            <Grid
                container
                spacing={1}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    direction: isRTL === "rtl" ? "rtl" : "ltr",
                }}
            >
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: 10,
                        marginBottom: 10,
                    }}
                >
                    <Card
                        style={{
                            borderRadius: "10px",
                            backgroundColor: colors.WHITE,
                            minHeight: 70,
                            minWidth: 300,
                            width: "80%",
                            color: colors.WHITE,
                            display: "flex",
                            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
                        }}
                    >
                        <Typography
                            style={{
                                fontSize: 20,
                                backgroundColor: MAIN_COLOR,
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "bold",
                                fontFamily:FONT_FAMILY
                            }}
                        >
                            {t("wallet_ballance")}
                        </Typography>
                        <Typography
                            style={{
                                fontSize: 30,
                                fontWeight: "bold",
                                color: colors.BLACK,
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontFamily:FONT_FAMILY
                            }}
                        >
                            {formattedBalance(walletBalance)}
                        </Typography>
                    </Card>
                </Grid>
                {canDebitWallet ? (
                    <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                        <Card style={{ padding: 20, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
                            <Typography style={{ fontWeight: 'bold', fontFamily: FONT_FAMILY, marginBottom: 10 }}>
                                Bakiyeden Düş
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label={t('amount')}
                                        type="number"
                                        value={debitAmount}
                                        onChange={(event) => setDebitAmount(event.target.value)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        label="Düşüm nedeni"
                                        value={debitReason}
                                        onChange={(event) => setDebitReason(event.target.value)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Button
                                        variant="contained"
                                        disabled={debitSubmitting}
                                        onClick={openDebitConfirmation}
                                        style={{ backgroundColor: colors.RED, color: colors.WHITE, fontFamily: FONT_FAMILY, height: '100%' }}
                                    >
                                        Bakiyeden Düş
                                    </Button>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                ) : null}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <div
                        style={{
                            backgroundColor: colors.WHITE,
                            borderRadius: "8px",
                            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
                        }}
                    >
                        <div
                            dir={isRTL === "rtl" ? "rtl" : "ltr"}
                        >
                            <Button
                                variant="text"
                                onClick={() => {
                                    navigate(`/users/${props.id}`);
                                }}
                            >
                                <Typography
                                    style={{
                                        textAlign: isRTL === "rtl" ? "right" : "left",
                                        fontWeight: "bold",
                                        color: MAIN_COLOR,
                                        fontFamily:FONT_FAMILY
                                    }}
                                >
                                    {`<<- ${t("go_back")}`}
                                </Typography>
                            </Button>
                        </div>
                        <ThemeProvider theme={theme}>
                            <MaterialTable
                                title={t("transaction_history_title")}
                                columns={columns}
                                style={{
                                    direction: isRTL === "rtl" ? "rtl" : "ltr",
                                    borderRadius: "8px",
                                    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
                                }}
                                data={walletHistory?walletHistory:[]}
                                onRowClick={(evt, selectedRow) =>
                                    setSelectedRow(selectedRow.tableData.id)
                                }
                                options={{
                                    pageSize: 10,
                                    pageSizeOptions: [10, 15, 20],
                                    exportButton: true,
                                    rowStyle: rowData => ({
                                        backgroundColor: (selectedRow === rowData.tableData.id) ? colors.THIRDCOLOR : colors.WHITE
                                    }),
                                    editable: {
                                        backgroundColor: colors.CARD_DETAIL,
                                        fontSize: "0.8em",
                                        fontWeight: "bold ",
                                    },
                                    headerStyle: {
                                        backgroundColor: SECONDORY_COLOR,
                                        color: colors.Black,
                                        fontSize: "0.8em",
                                        fontWeight: "bold ",
                                        border: `1px solid ${colors.TABLE_BORDER}`,
                                        textAlign: "center",
                                    },
                                    cellStyle: {
                                        border: `1px solid ${colors.TABLE_BORDER}`,
                                        textAlign: "center",
                                    },
                                }}
                                localization={{
                                    toolbar: {
                                        searchPlaceholder: t("search"),
                                        exportTitle: t("export"),
                                    },
                                    pagination: {
                                        labelDisplayedRows: "{from}-{to} " + t("of") + " {count}",
                                        firstTooltip: t("first_page_tooltip"),
                                        previousTooltip: t("previous_page_tooltip"),
                                        nextTooltip: t("next_page_tooltip"),
                                        lastTooltip: t("last_page_tooltip"),
                                    },
                                }}
                            />
                        </ThemeProvider>
                    </div>
                </Grid>
            </Grid>
            <Dialog open={debitConfirmationOpen} onClose={() => {
                if (!debitSubmitting) {
                    debitIdempotencyKey.current = null;
                    setDebitConfirmationOpen(false);
                }
            }}>
                <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Bakiye düşümünü onayla</DialogTitle>
                <DialogContent>
                    <Typography style={{ fontFamily: FONT_FAMILY }}>
                        {`${data?.firstName || ''} ${data?.lastName || ''}`.trim()} için {formattedBalance(parsedDebitAmount)} düşülecek.
                    </Typography>
                    <Typography style={{ fontFamily: FONT_FAMILY, marginTop: 8 }}>
                        Mevcut bakiye: {formattedBalance(currentBalance)} — Yeni bakiye: {formattedBalance(projectedBalance)}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        debitIdempotencyKey.current = null;
                        setDebitConfirmationOpen(false);
                    }} disabled={debitSubmitting}>{t('cancel')}</Button>
                    <Button onClick={confirmDebit} disabled={debitSubmitting} style={{ color: colors.RED }}>
                        {debitSubmitting ? t('saving') : 'Bakiyeden Düş'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: '' })}>
                <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Uyarı</DialogTitle>
                <DialogContent><Typography style={{ fontFamily: FONT_FAMILY }}>{commonAlert.msg}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setCommonAlert({ open: false, msg: '' })}>{t('close')}</Button></DialogActions>
            </Dialog>
        </Card>

    );
}

export default UserWalletDetails;
